import { ref, unref } from "vue";
import router from ".";
import { getToken } from "../apollo-client/apollo-token";

const LAST_ROUTE_KEY = "lastRoute";

// Set true when the user is bounced to login by an expired/rejected session
// (as opposed to an explicit logout or a first-time visit). PageLogin reads this
// to explain why they're back at the login screen.
export const sessionExpired = ref(false);

// Backed by sessionStorage (tab-scoped) so an intended deep link survives a hard
// reload — notably the 2FA-setup `window.location.assign`, which would otherwise
// drop the in-memory value.
export const $lastRoute = ref(restoreLastRoute());

function restoreLastRoute() {
  try {
    const stored = window.sessionStorage.getItem(LAST_ROUTE_KEY);
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
}

export function addBeforeNavigationAuthHook() {
  router.beforeEach((to, from, next) => {
    if (to.path === "/mobile-inventory") {
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get("token");
      if (urlToken || to.meta.withoutAuth) {
        next();
        return;
      }
    }

    if (to.meta.withoutAuth || getToken()) {
      next();
    } else {
      setLastRoute(to);
      next({ name: "Login" });
    }
  });
}

export function redirectToLogin() {
  if (window.location.pathname === "/mobile-inventory") {
    return;
  }
  if (router.currentRoute?.name !== "Login") {
    // Only surface "session expired" when bounced out of an AUTHENTICATED route
    // by a rejected/expired token. A redirect from a public page — e.g. the
    // password-reset success path or forgot-password, which reuse this helper as
    // a plain "go to login" — is not an expired session and must not show it.
    if (!router.currentRoute?.meta?.withoutAuth) {
      sessionExpired.value = true;
    }
    setLastRoute(router.currentRoute);
    router.replace({ name: "Login" });
  }
}

export function setLastRoute(routeRef) {
  const route = unref(routeRef);
  if (route && !route.meta?.withoutAuth) {
    // Persist only the serializable, navigable parts.
    const saved = {
      name: route.name,
      params: route.params,
      query: route.query,
      path: route.path,
    };
    $lastRoute.value = saved;
    try {
      window.sessionStorage.setItem(LAST_ROUTE_KEY, JSON.stringify(saved));
    } catch {
      // sessionStorage unavailable (private mode / SSR) — the in-memory ref
      // still works for the non-reload paths.
    }
  }
}

export function clearLastRoute() {
  $lastRoute.value = undefined;
  try {
    window.sessionStorage.removeItem(LAST_ROUTE_KEY);
  } catch {
    // ignore
  }
}
